const a = 6378137.0
const eccSquared = 0.00669438
const eccPrimeSquared = eccSquared / (1 - eccSquared)
const k0 = 0.9996

export function utmZoneNumber(lng: number): number {
  if (lng === 180) return 60
  return Math.min(60, Math.max(1, Math.floor((lng + 180) / 6) + 1))
}

export function utmCentralMeridian(zone: number): number {
  return (zone - 1) * 6 - 180 + 3
}

export interface UtmCoord {
  zone: number
  easting: number
  northing: number
  isNorth: boolean
}

export function latLngToUtm(lat: number, lng: number, forceZone?: number): UtmCoord {
  const zone = forceZone ?? utmZoneNumber(lng)
  const isNorth = lat >= 0

  const latRad = (lat * Math.PI) / 180
  const longOriginRad = (utmCentralMeridian(zone) * Math.PI) / 180

  const sinLat = Math.sin(latRad)
  const cosLat = Math.cos(latRad)
  const tanLat = Math.tan(latRad)

  const N = a / Math.sqrt(1 - eccSquared * sinLat * sinLat)
  const T = tanLat * tanLat
  const C = eccPrimeSquared * cosLat * cosLat
  const A = cosLat * ((lng * Math.PI) / 180 - longOriginRad)

  const M =
    a *
    ((1 - eccSquared / 4 - (3 * eccSquared ** 2) / 64 - (5 * eccSquared ** 3) / 256) * latRad -
      ((3 * eccSquared) / 8 + (3 * eccSquared ** 2) / 32 + (45 * eccSquared ** 3) / 1024) *
        Math.sin(2 * latRad) +
      ((15 * eccSquared ** 2) / 256 + (45 * eccSquared ** 3) / 1024) * Math.sin(4 * latRad) -
      ((35 * eccSquared ** 3) / 3072) * Math.sin(6 * latRad))

  const easting =
    k0 *
      N *
      (A +
        ((1 - T + C) * A ** 3) / 6 +
        ((5 - 18 * T + T * T + 72 * C - 58 * eccPrimeSquared) * A ** 5) / 120) +
    500000

  let northing =
    k0 *
    (M +
      N *
        tanLat *
        ((A * A) / 2 +
          ((5 - T + 9 * C + 4 * C * C) * A ** 4) / 24 +
          ((61 - 58 * T + T * T + 600 * C - 330 * eccPrimeSquared) * A ** 6) / 720))
  if (!isNorth) northing += 10000000

  return { zone, easting, northing, isNorth }
}

export function utmToLatLng(
  easting: number,
  northing: number,
  zone: number,
  isNorth: boolean
): [number, number] {
  const e1 = (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared))

  const x = easting - 500000
  const y = isNorth ? northing : northing - 10000000

  const longOrigin = utmCentralMeridian(zone)

  const M = y / k0
  const mu =
    M / (a * (1 - eccSquared / 4 - (3 * eccSquared ** 2) / 64 - (5 * eccSquared ** 3) / 256))

  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
    ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
    ((151 * e1 ** 3) / 96) * Math.sin(6 * mu)

  const sinPhi1 = Math.sin(phi1)
  const cosPhi1 = Math.cos(phi1)
  const tanPhi1 = Math.tan(phi1)

  const N1 = a / Math.sqrt(1 - eccSquared * sinPhi1 * sinPhi1)
  const T1 = tanPhi1 * tanPhi1
  const C1 = eccPrimeSquared * cosPhi1 * cosPhi1
  const R1 = (a * (1 - eccSquared)) / Math.pow(1 - eccSquared * sinPhi1 * sinPhi1, 1.5)
  const D = x / (N1 * k0)

  const lat =
    phi1 -
    ((N1 * tanPhi1) / R1) *
      ((D * D) / 2 -
        ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * eccPrimeSquared) * D ** 4) / 24 +
        ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * eccPrimeSquared - 3 * C1 * C1) * D ** 6) /
          720)

  const lon =
    (D -
      ((1 + 2 * T1 + C1) * D ** 3) / 6 +
      ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * eccPrimeSquared + 24 * T1 * T1) * D ** 5) / 120) /
    cosPhi1

  return [longOrigin + (lon * 180) / Math.PI, (lat * 180) / Math.PI]
}

export interface GridLineResult {
  features: GeoJSON.Feature[]
}

export function generateSubZoneGrid(
  zone: number,
  isNorth: boolean,
  bounds: { west: number; east: number; south: number; north: number },
  interval: number,
  lineType: string
): GeoJSON.Feature[] {
  const features: GeoJSON.Feature[] = []

  const zoneWestLng = (zone - 1) * 6 - 180
  const zoneEastLng = zoneWestLng + 6
  const clipWest = Math.max(bounds.west, zoneWestLng + 0.01)
  const clipEast = Math.min(bounds.east, zoneEastLng - 0.01)
  const clipSouth = isNorth ? Math.max(0.01, bounds.south) : bounds.south
  const clipNorth = isNorth ? bounds.north : Math.min(-0.01, bounds.north)

  if (clipWest >= clipEast || clipSouth >= clipNorth) return features

  const sw = latLngToUtm(clipSouth, clipWest, zone)
  const nw = latLngToUtm(clipNorth, clipWest, zone)
  const se = latLngToUtm(clipSouth, clipEast, zone)
  const ne = latLngToUtm(clipNorth, clipEast, zone)

  const minE = Math.floor(Math.min(sw.easting, nw.easting) / interval) * interval
  const maxE = Math.ceil(Math.max(se.easting, ne.easting) / interval) * interval
  const minN = Math.floor(Math.min(sw.northing, se.northing) / interval) * interval
  const maxN = Math.ceil(Math.max(nw.northing, ne.northing) / interval) * interval

  const safeMinE = Math.max(100000, minE)
  const safeMaxE = Math.min(900000, maxE)
  const safeMinN = isNorth ? Math.max(0, minN) : Math.max(1100000, minN)
  const safeMaxN = isNorth ? Math.min(9400000, maxN) : Math.min(10000000, maxN)

  const sampleDist = Math.max(interval / 5, 1000)

  // Clip generated coords to zone longitude range, splitting into
  // separate segments when points fall outside the zone.
  const clipToZone = (coords: [number, number][]): [number, number][][] => {
    const segments: [number, number][][] = []
    let current: [number, number][] = []
    for (const pt of coords) {
      if (pt[0] >= zoneWestLng && pt[0] <= zoneEastLng) {
        current.push(pt)
      } else if (current.length > 0) {
        segments.push(current)
        current = []
      }
    }
    if (current.length > 0) segments.push(current)
    return segments
  }

  for (let e = safeMinE; e <= safeMaxE; e += interval) {
    const raw: [number, number][] = []
    for (let n = safeMinN; n <= safeMaxN; n += sampleDist) {
      try {
        const [lng, lat] = utmToLatLng(e, n, zone, isNorth)
        if (lat >= -80 && lat <= 84) raw.push([lng, lat])
      } catch {
        /* skip invalid */
      }
    }
    for (const coords of clipToZone(raw)) {
      if (coords.length >= 2) {
        features.push({
          type: 'Feature',
          properties: {
            type: lineType,
            direction: 'easting',
            value: e,
            level: interval,
            label: `${Math.round(e / 1000)}`,
          },
          geometry: { type: 'LineString', coordinates: coords },
        })
      }
    }
  }

  for (let n = safeMinN; n <= safeMaxN; n += interval) {
    const raw: [number, number][] = []
    for (let e = safeMinE; e <= safeMaxE; e += sampleDist) {
      try {
        const [lng, lat] = utmToLatLng(e, n, zone, isNorth)
        if (lat >= -80 && lat <= 84) raw.push([lng, lat])
      } catch {
        /* skip invalid */
      }
    }
    for (const coords of clipToZone(raw)) {
      if (coords.length >= 2) {
        features.push({
          type: 'Feature',
          properties: {
            type: lineType,
            direction: 'northing',
            value: n,
            level: interval,
            label: `${Math.round(n / 1000)}`,
          },
          geometry: { type: 'LineString', coordinates: coords },
        })
      }
    }
  }

  return features
}
